/**
 * knobDrag mixin
 *
 * Provides drag methods for pedal rotary knob components.
 *
 * Three modes:
 *   startKnobDrag(event, getter, setter, min, max, snaps)
 *     Unipolar — existing behaviour (default). Range min→max, optional snap points.
 *
 *   startBipolarKnobDrag(event, getter, setter, range = 100, snaps = [0])
 *     Bipolar — -range to +range (default -100 to +100). Snaps at zero by default.
 *
 *   startEnumKnobDrag(event, getter, setter, options)
 *     Enum — cycles through options array. One option per ~20px of drag.
 *
 * Dragging upward increases the value; dragging downward decreases it.
 * A full 100px drag spans the full unipolar range (min → max).
 *
 * snaps: optional array of values at which the knob "clicks". The knob snaps
 * to the nearest snap point when within SNAP_ZONE px of it, then releases
 * once the drag moves SNAP_RELEASE px past the snap value.
 */

const SNAP_ZONE         = 4;   // px within which a snap activates
const SNAP_RELEASE      = 6;   // px past the snap value before the snap releases
const BIPOLAR_SNAP_ZONE = 4;   // px within which bipolar zero-snap activates
const ENUM_PX_PER_STEP  = 20;  // px of drag required to advance one enum option

export default {
  methods: {
    // ------------------------------------------------------------------ //
    // UNIPOLAR (existing)
    // logarithmic: when true, value is distributed logarithmically across
    // min→max (min and max must both be > 0). The setter receives the actual
    // value (not a log-space intermediate). Snaps are not supported in log mode.
    // ------------------------------------------------------------------ //
    startKnobDrag(event, getter, setter, min = 0, max = 127, snaps = [], logarithmic = false) {
      event.preventDefault();
      const startValue = getter() ?? min;
      const startY = event.clientY ?? event.touches?.[0]?.clientY;

      if (logarithmic) {
        // Work entirely in log space; 100px spans the full range.
        const logMin  = Math.log(min);
        const logMax  = Math.log(max);
        const logRange = logMax - logMin;
        const startLogValue = Math.log(Math.max(min, startValue));
        const sensitivity = logRange / 100;

        const onMove = (e) => {
          const clientY = e.clientY ?? e.touches?.[0]?.clientY ?? startY;
          const dy = startY - clientY;
          const logValue = Math.min(logMax, Math.max(logMin, startLogValue + dy * sensitivity));
          const value = Math.exp(logValue);
          // Round to 2 decimal places for display cleanliness.
          setter(Math.round(value * 100) / 100);
        };

        const onUp = () => {
          window.removeEventListener('mousemove', onMove);
          window.removeEventListener('mouseup', onUp);
          window.removeEventListener('touchmove', onMove);
          window.removeEventListener('touchend', onUp);
          window.removeEventListener('touchcancel', onUp);
        };

        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
        window.addEventListener('touchmove', onMove, { passive: false });
        window.addEventListener('touchend', onUp);
        window.addEventListener('touchcancel', onUp);
        return;
      }

      const range = max - min;
      const sensitivity = range / 100; // units per px

      // Track which snap point is currently holding the knob, if any.
      // null = free; a number = the snap value we are locked on.
      let activeSnap = null;

      const onMove = (e) => {
        const clientY = e.clientY ?? e.touches?.[0]?.clientY ?? startY;
        const dy = startY - clientY;
        const rawValue = Math.min(max, Math.max(min, startValue + dy * sensitivity));

        if (snaps.length === 0) {
          setter(Math.round(rawValue));
          return;
        }

        // If we are currently locked on a snap, check whether the drag has
        // moved far enough past it to release.
        if (activeSnap !== null) {
          const overshootPx = (rawValue - activeSnap) / sensitivity;
          if (Math.abs(overshootPx) >= SNAP_RELEASE) {
            activeSnap = null;
            // Fall through to free movement below.
          } else {
            setter(activeSnap);
            return;
          }
        }

        // Free movement — check if we're close enough to any snap point.
        for (const snap of snaps) {
          const distPx = Math.abs(rawValue - snap) / sensitivity;
          if (distPx <= SNAP_ZONE) {
            activeSnap = snap;
            setter(snap);
            return;
          }
        }

        setter(Math.round(rawValue));
      };

      const onUp = () => {
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
        window.removeEventListener('touchmove', onMove);
        window.removeEventListener('touchend', onUp);
        window.removeEventListener('touchcancel', onUp);
      };

      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
      window.addEventListener('touchmove', onMove, { passive: false });
      window.addEventListener('touchend', onUp);
      window.addEventListener('touchcancel', onUp);
    },

    // ------------------------------------------------------------------ //
    // BIPOLAR  (-range … 0 … +range; range defaults to 100)
    // `snaps` are the values the knob clicks onto (default: just zero at the
    // centre). A full 100px drag spans the whole -range … +range sweep.
    // ------------------------------------------------------------------ //
    startBipolarKnobDrag(event, getter, setter, range = 100, snaps = [0]) {
      event.preventDefault();
      const startY = event.clientY ?? event.touches?.[0]?.clientY;
      const startValue = getter() ?? 0;
      // 2 * range units over 100px (200 over 100px = 2 units/px at range 100)
      const sensitivity = (2 * range) / 100;
      const snapPoints = snaps.length > 0 ? snaps : [0];
      let activeSnap = null;

      const onMove = (e) => {
        const clientY = e.clientY ?? e.touches?.[0]?.clientY ?? startY;
        const dy = startY - clientY;
        const rawValue = Math.min(range, Math.max(-range, startValue + dy * sensitivity));

        // Held on a snap: release once the drag has moved SNAP_RELEASE px past it.
        if (activeSnap !== null) {
          if (Math.abs(rawValue - activeSnap) / sensitivity >= SNAP_RELEASE) {
            activeSnap = null;
          } else {
            setter(activeSnap);
            return;
          }
        }

        // Free movement: activate a snap when within BIPOLAR_SNAP_ZONE px of it.
        for (const snap of snapPoints) {
          if (Math.abs(rawValue - snap) / sensitivity <= BIPOLAR_SNAP_ZONE) {
            activeSnap = snap;
            setter(snap);
            return;
          }
        }

        setter(Math.round(rawValue));
      };

      const onUp = () => {
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
        window.removeEventListener('touchmove', onMove);
        window.removeEventListener('touchend', onUp);
        window.removeEventListener('touchcancel', onUp);
      };

      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
      window.addEventListener('touchmove', onMove, { passive: false });
      window.addEventListener('touchend', onUp);
      window.addEventListener('touchcancel', onUp);
    },

    // ------------------------------------------------------------------ //
    // ENUM  (cycles through options array)
    // ------------------------------------------------------------------ //
    startEnumKnobDrag(event, getter, setter, options, circular = false) {
      event.preventDefault();
      if (!options || options.length === 0) return;

      const startY = event.clientY ?? event.touches?.[0]?.clientY;
      const currentValue = getter();
      const startIndex = Math.max(0, options.findIndex(o => o.value === currentValue));
      const n = options.length;

      const onMove = (e) => {
        const clientY = e.clientY ?? e.touches?.[0]?.clientY ?? startY;
        const dy = startY - clientY; // positive = dragged up = higher index
        const indexDelta = Math.round(dy / ENUM_PX_PER_STEP);
        if (circular) {
          // Wrap-around: modulo keeps index within [0, n)
          const newIndex = ((startIndex + indexDelta) % n + n) % n;
          setter(options[newIndex].value);
        } else {
          const newIndex = Math.min(n - 1, Math.max(0, startIndex + indexDelta));
          setter(options[newIndex].value);
        }
      };

      const onUp = () => {
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
        window.removeEventListener('touchmove', onMove);
        window.removeEventListener('touchend', onUp);
        window.removeEventListener('touchcancel', onUp);
      };

      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
      window.addEventListener('touchmove', onMove, { passive: false });
      window.addEventListener('touchend', onUp);
      window.addEventListener('touchcancel', onUp);
    }
  }
};
